import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [member, setMember] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('frontend'); // 'frontend' | 'backend'

  useEffect(() => {
    const savedMember = localStorage.getItem('ucc_member');
    const savedToken = localStorage.getItem('ucc_token');
    if (savedMember && savedToken) {
      try {
        setMember(JSON.parse(savedMember));
        // Always refresh from server to get latest role/permissions
        api.get('/auth/me').then(res => {
          setMember(res.data.member);
          setPermissions(res.data.permissions || []);
          localStorage.setItem('ucc_member', JSON.stringify(res.data.member));
          if (res.data.token) localStorage.setItem('ucc_token', res.data.token);
        }).catch((err) => {
          // Only clear session if token is explicitly invalid/expired
          // Do NOT logout on network errors or temporary failures
          const status = err.response?.status;
          const errMsg = err.response?.data?.error || '';
          const isReallyInvalid = status === 401 && (
            errMsg.includes('expired') || 
            errMsg.includes('Invalid authentication') ||
            errMsg.includes('Member not found')
          );
          if (isReallyInvalid) {
            localStorage.removeItem('ucc_member');
            localStorage.removeItem('ucc_token');
            setMember(null);
          }
          // Otherwise keep the user logged in — network blip, timeout, etc.
        });
      } catch {
        localStorage.removeItem('ucc_member');
      }
    }
    setLoading(false);

    // Auto-refresh role & permissions every 60 seconds — no logout needed after role change
    const interval = setInterval(() => {
      const token = localStorage.getItem('ucc_token');
      if (!token) return;
      api.get('/auth/me').then(res => {
        setMember(res.data.member);
        setPermissions(res.data.permissions || []);
        localStorage.setItem('ucc_member', JSON.stringify(res.data.member));
        if (res.data.token) localStorage.setItem('ucc_token', res.data.token);
      }).catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const login = useCallback((token, memberData, perms = []) => {
    localStorage.setItem('ucc_token', token);
    localStorage.setItem('ucc_member', JSON.stringify(memberData));
    setMember(memberData);
    setPermissions(perms);
  }, []);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch {}
    localStorage.removeItem('ucc_token');
    localStorage.removeItem('ucc_member');
    setMember(null);
    setPermissions([]);
    setMode('frontend');
  }, []);

  const refreshMember = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setMember(res.data.member);
      setPermissions(res.data.permissions || []);
      localStorage.setItem('ucc_member', JSON.stringify(res.data.member));
    } catch {}
  }, []);

  const hasPermission = useCallback((module, action) => {
    if (member?.role === 'super_admin') return true;
    const perm = permissions.find(p => p.module === module);
    return perm?.[action] || false;
  }, [member, permissions]);

  const isAdmin = member?.role === 'super_admin' || member?.role === 'admin';
  const isSuperAdmin = member?.role === 'super_admin';
  const isObserver = member?.is_observer;

  return (
    <AuthContext.Provider value={{
      member, permissions, loading, mode, setMode,
      login, logout, refreshMember, hasPermission,
      isAdmin, isSuperAdmin, isObserver,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
